import { useEffect, useRef, useState, type FC, type ReactNode } from "react";
import StyledTooltip from "./StyledTooltip";

const CLIPPING_ANCESTOR_LEVELS = 3;

const isClipped = (element: HTMLElement) => {
  let node: HTMLElement | null = element;
  for (let level = 0; node && level <= CLIPPING_ANCESTOR_LEVELS; level += 1) {
    if (node.scrollWidth > node.clientWidth) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
};

interface TruncatedTooltipTextProps {
  text: string;
  className: string;
  children: ReactNode;
}

/** Shows the tooltip only while the text, or the box that clips it, is cut off with an ellipsis. */
const TruncatedTooltipText: FC<TruncatedTooltipTextProps> = ({ text, className, children }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }
    const measure = () => setIsTruncated(isClipped(element));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (element.parentElement) {
      observer.observe(element.parentElement);
    }
    return () => observer.disconnect();
  }, [text]);

  return (
    <StyledTooltip content={text || undefined} disabled={!isTruncated}>
      <span ref={textRef} className={className}>
        {children}
      </span>
    </StyledTooltip>
  );
};

export default TruncatedTooltipText;
